import { Box, Center, Heading, Icon, Image, Stack, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { ProductImage } from '../models/ProductImage.ts';
import { AvailableFormModel } from '../productView/models/ProductListingModel.ts';

export type ProductMetaProps = {
  name: string;
  forms: AvailableFormModel[];
  usages: string[];
  image?: ProductImage;
};

const NoImagePlaceholder = () => (
  <Center width='full' height='full' bg='gray.50' borderRadius='lg'>
    <Icon viewBox='0 0 24 24' boxSize='12' color='gray.300'>
      <path
        d='M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z'
        strokeWidth='1.5'
        stroke='currentColor'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </Icon>
  </Center>
);

export const ProductMeta = (props: ProductMetaProps) => {
  const { image, name, forms, usages } = props;
  const uniqueForms = forms.filter(
    (form, index, self) => self.findIndex((f) => f.Name === form.Name) === index
  );
  return (
    <Stack direction='row' gap={{ base: '4', md: '5' }} width='full' align='center'>
      <Box
        width={{ base: '20', md: '28' }}
        height={{ base: '20', md: '28' }}
        rounded='xl'
        overflow='hidden'
        flexShrink={0}
        bg='gray.50'>
        {image ? (
          <Image
            fit='cover'
            src={image.Url}
            alt={image.AltText}
            draggable='false'
            loading='lazy'
            width='full'
            height='full'
          />
        ) : (
          <NoImagePlaceholder />
        )}
      </Box>

      <Stack gap='2' flex='1' minW='0'>
        <Heading as='h3' size={{ base: 'sm', md: 'md' }} fontWeight='semibold' lineClamp={2}>
          {name}
        </Heading>
        {forms && forms.length > 0 && (
          <Stack gap='1'>
            <Wrap gap='2'>
              {uniqueForms.slice(0, 3).map((form) => {
                const dayLabel = form.ExpiresInDays === 1 ? 'day' : 'days';
                return (
                  <WrapItem key={form.Name}>
                    <Box
                      px='2.5'
                      py='2'
                      borderWidth='1px'
                      borderColor='blue.100'
                      borderRadius='md'
                      bg='blue.50'
                      minW={{ base: '140px', md: '160px' }}
                      maxW={{ base: '220px', md: '260px' }}>
                      <Text fontSize='xs' fontWeight='semibold' color='blue.700' lineClamp={1}>
                        {form.Name}
                      </Text>
                      {form.ExpiresInDays != null && (
                        <Text fontSize='xs' color='gray.600' lineClamp={1}>
                          Expires in {form.ExpiresInDays} {dayLabel}
                        </Text>
                      )}
                      {form.Description && (
                        <Text fontSize='xs' color='gray.500' lineClamp={2}>
                          {form.Description}
                        </Text>
                      )}
                    </Box>
                  </WrapItem>
                );
              })}
            </Wrap>
            {uniqueForms.length > 3 && (
              <Text fontSize='xs' color='gray.500'>
                +{uniqueForms.length - 3} more forms
              </Text>
            )}
          </Stack>
        )}
        {usages && usages.length > 0 && (
          <Text fontSize='sm' color='gray.600' lineClamp={1}>
            {[...new Set(usages)].join(' • ')}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
